import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Percent, Loader2, Trash2, FileDown, Plus, X, CheckCircle2, Eye } from "lucide-react";
import { fetchProvisions, deleteProvision, downloadProvision, markProvisionPaid, computeProvision, createProvisionFromPayout } from "../lib/provisionEngine";
import { fetchArtists } from "../lib/driveClient";
import { supabase } from "../../lib/supabase";

const formatEUR = (v) => (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const STATUS = { draft: "Entwurf", issued: "Ausgestellt", paid: "Bezahlt", cancelled: "Storniert" };
const STATUS_COLOR = {
  draft: "text-zinc-400 border-white/15",
  issued: "text-yellow-400 border-yellow-500/40",
  paid: "text-emerald-400 border-emerald-500/40",
  cancelled: "text-zinc-600 border-white/10",
};

export default function DriveProvisions() {
  const [rows, setRows] = useState([]);
  const [artists, setArtists] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterArtist, setFilterArtist] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, mh] = await Promise.all([
        fetchProvisions(),
        fetchArtists(),
        supabase.from("drive_music_hub_payouts").select("*").order("created_at", { ascending: false }).then((r) => r.data || []),
      ]);
      setRows(p); setArtists(a); setPayouts(mh);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!filterArtist) return rows;
    return rows.filter((r) => r.artist_id === filterArtist);
  }, [rows, filterArtist]);

  const groupedByArtist = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const key = r.artist_id || "unbekannt";
      const label = r.drive_artists?.artist_name || r.artist_snapshot?.display_name || "Unbekannt";
      (g[key] ||= { label, rows: [] }).rows.push(r);
    }
    return g;
  }, [filtered]);

  const totals = useMemo(() => {
    return filtered.reduce((s, r) => ({
      gross: s.gross + Number(r.gross_amount || 0),
      label: s.label + Number(r.label_amount || 0),
      artist: s.artist + Number(r.artist_amount || 0),
    }), { gross: 0, label: 0, artist: 0 });
  }, [filtered]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Provisionsabrechnung ${row.statement_number} wirklich löschen?`)) return;
    setBusy(`del-${row.id}`);
    try { await deleteProvision(row); toast.success("Gelöscht"); await load(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const handleDownload = async (row) => {
    setBusy(`dl-${row.id}`);
    try { await downloadProvision(row); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const handlePreview = async (row) => {
    if (!row.storage_path) { toast.error("Keine Datei"); return; }
    setBusy(`pv-${row.id}`);
    try {
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(row.storage_path, 300);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const handleMarkPaid = async (row) => {
    setBusy(`paid-${row.id}`);
    try { await markProvisionPaid(row.id); toast.success("Als bezahlt markiert"); await load(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-3">Buchhaltung</div>
        <div className="font-display font-black uppercase text-3xl md:text-4xl mb-2" data-testid="provisions-title">Provisionsabrechnungen</div>
        <div className="text-sm text-zinc-400 max-w-2xl">
          Automatisch generierte Abrechnungen an deine Künstler auf Basis der MusicHub-Auszahlungen. Individueller Prozentsatz je Künstler, sortiert nach Monat.
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Abrechnungen" value={filtered.length} />
        <StatCard label="Brutto gesamt" value={formatEUR(totals.gross)} />
        <StatCard label="Label-Anteil" value={formatEUR(totals.label)} />
        <StatCard label="Künstler-Auszahlungen" value={formatEUR(totals.artist)} accent />
      </div>

      {/* Filter + Add */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-provision"
          className="inline-flex items-center gap-3 bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase transition-colors"
        >
          <Plus size={14} /> Neue Abrechnung
        </button>
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Filter:</label>
          <select value={filterArtist} onChange={(e) => setFilterArtist(e.target.value)} className="bg-[#050505] border border-white/10 px-3 py-2 text-sm text-white outline-none">
            <option value="">Alle Künstler</option>
            {artists.map((a) => <option key={a.id} value={a.id}>{a.artist_name}</option>)}
          </select>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center gap-3 text-zinc-400 text-sm"><Loader2 size={16} className="animate-spin" /> Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/[0.06] px-6 py-10 text-center text-sm text-zinc-500 italic">
          Noch keine Provisionsabrechnungen. Klick oben auf „Neue Abrechnung".
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByArtist).map(([artistId, group]) => (
            <div key={artistId} className="bg-[#0A0A0A] border border-white/[0.06]">
              <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
                <div className="font-display font-black uppercase text-lg text-white">{group.label}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{group.rows.length} × Abrechnungen</div>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {group.rows.map((r) => (
                  <div key={r.id} className="grid grid-cols-1 md:grid-cols-[140px_120px_1fr_130px_120px_auto] gap-3 items-center px-5 py-3" data-testid={`prov-${r.id}`}>
                    <div>
                      <div className="text-sm text-white font-bold">{r.statement_number}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{r.month}</div>
                    </div>
                    <div className="text-sm text-zinc-300">
                      <span className={`inline-block text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 border ${STATUS_COLOR[r.status] || ""}`}>{STATUS[r.status] || r.status}</span>
                    </div>
                    <div className="text-xs text-zinc-500 min-w-0 truncate">{r.source_label || "—"}</div>
                    <div className="text-right text-sm text-white tabular-nums">
                      <div className="text-[10px] text-zinc-500">Brutto</div>
                      {formatEUR(r.gross_amount)}
                    </div>
                    <div className="text-right text-sm text-[#ff0033] font-bold tabular-nums">
                      <div className="text-[10px] text-zinc-500 font-normal">Auszahlung</div>
                      {formatEUR(r.artist_amount)}
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      {r.status !== "paid" && (
                        <button onClick={() => handleMarkPaid(r)} disabled={busy === `paid-${r.id}`} title="Als bezahlt markieren" className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors disabled:opacity-40">
                          {busy === `paid-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                        </button>
                      )}
                      <button onClick={() => handlePreview(r)} disabled={busy === `pv-${r.id}`} title="Vorschau" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
                        {busy === `pv-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                      </button>
                      <button onClick={() => handleDownload(r)} disabled={busy === `dl-${r.id}`} title="PDF herunterladen" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
                        {busy === `dl-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
                      </button>
                      <button onClick={() => handleDelete(r)} disabled={busy === `del-${r.id}`} title="Löschen" className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-[#ff0033]/50 hover:text-[#ff0033] transition-colors disabled:opacity-40">
                        {busy === `del-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <CreateModal artists={artists} payouts={payouts} onClose={() => setShowModal(false)} onDone={async () => { setShowModal(false); await load(); }} />}
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className={`p-5 border ${accent ? "bg-[#ff0033]/[0.06] border-[#ff0033]/30" : "bg-[#0A0A0A] border-white/[0.06]"}`}>
    <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">{label}</div>
    <div className={`font-display font-black text-2xl ${accent ? "text-[#ff0033]" : "text-white"}`}>{value}</div>
  </div>
);

export const CreateModal = ({ artists, payouts, prefillPayout, prefillArtistId, onClose, onDone }) => {
  const [artistId, setArtistId] = useState(prefillArtistId || "");
  const [payoutId, setPayoutId] = useState(prefillPayout?.id || "");
  const [gross, setGross] = useState(prefillPayout?.amount ? String(prefillPayout.amount) : "");
  const [percent, setPercent] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const artist = artists.find((a) => a.id === artistId);
  const payout = payouts.find((p) => p.id === payoutId) || prefillPayout;

  useEffect(() => {
    if (artist && !percent) setPercent(String(artist.commission_percent ?? 40));
  }, [artist]); // eslint-disable-line

  useEffect(() => {
    if (payout && !gross) setGross(String(payout.amount || 0));
  }, [payout]); // eslint-disable-line

  const preview = computeProvision(gross, percent);

  const submit = async () => {
    if (!artist) { toast.error("Bitte Künstler wählen"); return; }
    if (!Number(gross)) { toast.error("Bruttobetrag ist Pflicht"); return; }
    setBusy(true);
    try {
      await createProvisionFromPayout({ payout, artist, commission_percent: percent, gross_amount: gross, notes });
      toast.success("Provisionsabrechnung erstellt");
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold">Provision</div>
            <div className="font-display font-black uppercase text-lg mt-1">Neue Abrechnung</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Künstler</label>
            <select value={artistId} onChange={(e) => { setArtistId(e.target.value); setPercent(""); }} data-testid="prov-artist" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60">
              <option value="">— wählen —</option>
              {artists.map((a) => <option key={a.id} value={a.id}>{a.artist_name} · Standard {a.commission_percent ?? 40} %</option>)}
            </select>
            {artist && !artist.iban && (
              <div className="mt-1 text-[10px] text-[#ff0033] tracking-wide">⚠ Keine IBAN im Künstler-Datensatz. Bitte im CRM ergänzen.</div>
            )}
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">MusicHub-Payout (optional)</label>
            <select value={payoutId} onChange={(e) => { setPayoutId(e.target.value); const p = payouts.find(x => x.id === e.target.value); if (p) setGross(String(p.amount || 0)); }} data-testid="prov-payout" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60">
              <option value="">— Ohne Payout / Frei —</option>
              {payouts.map((p) => <option key={p.id} value={p.id}>{p.month} · {p.title || p.file_name} · {formatEUR(p.amount)}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Bruttobetrag (€)</label>
              <input type="number" step="0.01" value={gross} onChange={(e) => setGross(e.target.value)} data-testid="prov-gross" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Label-Anteil (%)</label>
              <input type="number" step="0.5" value={percent} onChange={(e) => setPercent(e.target.value)} data-testid="prov-percent" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
            </div>
          </div>

          {/* Preview */}
          <div className="bg-[#050505] border border-white/[0.06] p-4 space-y-2">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Vorschau</div>
            <div className="flex justify-between text-sm"><span className="text-zinc-400">Brutto</span><span className="text-white tabular-nums">{formatEUR(gross)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-400">Label ({Number(percent) || 0} %)</span><span className="text-zinc-400 tabular-nums">− {formatEUR(preview.label)}</span></div>
            <div className="flex justify-between text-base font-bold pt-2 border-t border-white/[0.06]"><span className="text-[#ff0033]">Auszahlung Künstler</span><span className="text-[#ff0033] tabular-nums">{formatEUR(preview.artist)}</span></div>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Notizen (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="prov-notes" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 hover:text-white">Abbrechen</button>
          <button onClick={submit} disabled={busy} data-testid="prov-submit" className="inline-flex items-center gap-2 bg-[#ff0033] text-white px-5 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Percent size={13} />}
            {busy ? "Erstelle…" : "Erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
};
